import React from 'react';
import { Alert, Button, Card, Checkbox, Drawer, InputNumber, Select, Space, Spin, Table, Tooltip, Typography, message } from 'antd';
import { PmsAPI } from '../../../../controller/PmsAPI';
import ResponsiveECharts from '../../../components/ResponsiveECharts';
import DateWithWeekday from '../../../components/DateWithWeekday';
import YearCalendar from './YearCalendar';
import { FONT } from "../../../styles/typography";
import {
    LEVEL_COLORS,
    LEVEL_LABELS,
    MANUAL_DOT_STYLE,
    SUGGESTED_BORDER,
    clearOverride,
    clearOverrides,
    loadOverrides,
    loadSettings,
    resolveDays,
    saveOverride,
    saveOverrides,
    saveSettings,
} from './rateCalendarState';

const THIS_YEAR = new Date().getFullYear();

/**
 * 預設近三個完整年度，再加上當年度已過的部分。
 *
 * 當年度是離目標年最近的資料，沒有理由不看。後端只會取到昨天為止 ——
 * RVUSE_DT 裡有未來日期的列 (實測一路到隔年 1 月初)，那是在手數不是實績，
 * 見 RateCalendarData::cutoffFor。旺季偵測對「還沒發生的日子」一律棄權，
 * 不會因為當年度後段沒資料就把整段旺季否決掉。
 */
const DEFAULT_BASE_YEARS = [THIS_YEAR - 3, THIS_YEAR - 2, THIS_YEAR - 1, THIS_YEAR];

/**
 * 最早有歷史實績的年度，取樣年度下拉的下限。
 *
 * 必須與後端 RateCalendarController::EARLIEST_DATA_YEAR 一致 —— 那個常數同時是
 * year 與 base_years.* 的 min 驗證規則。列出比它更早的年度，使用者選到就是必定
 * 422，而查詢失敗時畫面只會停在舊資料上。回應沒有把這個值帶出來，只能兩邊各存
 * 一份；改後端那個常數時這裡要跟著改。
 */
const EARLIEST_DATA_YEAR = 2019;

/** 取樣年度下拉最多往回列幾年。 */
const BASE_YEAR_CHOICES = 8;

/**
 * 在手率的提前天數。只查 60 天這一檔 —— 規格說它是「住房率撞天花板時唯一分得出
 * 高下的判準」，休閒飯店的旺日與旺假日常常都在 95~100%，這時看提前 60 天賣掉多少
 * 才分得出來。多查幾檔只是讓這支本來就貴的查詢更貴。
 */
const PACE_LEAD = 60;

/**
 * 一次能查幾個日期。必須與後端 RateCalendarData::MAX_PACE_DATES 一致 ——
 * 後端把它當成 dates 的 max 驗證規則，超過就是 422。建議清單的長度天然就在
 * 這個數字以內，但清單長度是資料決定的，不該假設它永遠夠短。
 */
const MAX_PACE_DATES = 60;

/**
 * 門檻改動到觸發查詢之間的等待時間。
 *
 * 四個門檻的 InputNumber 每次 onChange 都會 setSettings，若主查詢 effect 直接依賴
 * settings，打「65」這種兩位數字就會連續觸發兩三次查詢。主查詢本身有 24 小時快取
 * 還好，但 pace 那支沒有快取且是數十秒等級的查詢 (見 RateCalendarData::paceOtb
 * docblock)，連打幾次就是對 PMS 主機的無謂負載。debounce 的是「觸發查詢」，不是
 * 輸入框的顯示值 —— settings 仍在每次 onChange 立即更新，使用者打字不卡頓。
 */
const SETTINGS_DEBOUNCE_MS = 300;

/**
 * 圖例用的簡寫。日曆每一格點開的下拉選單本身就是
 * 「平日 / 旺日 / 假日 / 旺假日 / 大假日」五個完整名稱，圖例若逐字重複，
 * 選單打開時畫面上會同時存在兩份一模一樣的文字節點。簡寫不會跟完整名稱
 * 逐字相同，圖例仍然不需要 hover 就看得懂，完整名稱則放進 title / aria-label。
 */
const LEVEL_ABBR = {
    normal: '平',
    'peak-season-normal': '旺',
    holiday: '假',
    'peak-season-holiday': '旺假',
    'special-holiday': '大假',
};

/**
 * 建議清單表格：不指定 width 全交給自動分配，會把「日期」「調整」這種短內容
 * 擠成兩三行 (例如 2027-01-01 斷成 2027-01- / 01)，讀的人會看錯位數。
 * 除了「理由」(說明文字，允許換行) 以外的欄位都固定寬度並套這個樣式強制不斷行，
 * 表頭跟儲存格都要套，斷行的問題兩邊都會出。寬度數值來自真實瀏覽器量測
 * (見本頁 task-24 報告)，不是憑感覺抓的。
 */
const NOWRAP_CELL = { style: { whiteSpace: 'nowrap' } };

export default function RateCalendarPage() {
    const [year, setYear] = React.useState(THIS_YEAR + 1);
    const [baseYears, setBaseYears] = React.useState(DEFAULT_BASE_YEARS);
    const [settings, setSettings] = React.useState(loadSettings);
    // 主查詢 effect 改看這一份，而不是 settings 本身 —— 見 SETTINGS_DEBOUNCE_MS
    // 的說明。初始值直接等於 settings，掛載時的第一次查詢不會被無謂延後。
    const [debouncedSettings, setDebouncedSettings] = React.useState(settings);
    const [overrides, setOverrides] = React.useState(() => loadOverrides(THIS_YEAR + 1));
    const [payload, setPayload] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [pace, setPace] = React.useState({ status: 'idle', data: {}, truncated: false });
    // 調整建議清單搬進 Drawer 後的開關。跟 pace 查詢的 effect 完全無關 ——
    // pace 綁在 payload 上，不管這個 Drawer 開不開都照查，見下方 pace effect
    // 的說明；不能改成「開了才查」，否則每次點開都要重新等一次數十秒的查詢。
    const [adjustmentsOpen, setAdjustmentsOpen] = React.useState(false);

    React.useEffect(() => {
        setOverrides(loadOverrides(year));
    }, [year]);

    React.useEffect(() => {
        const timer = setTimeout(() => setDebouncedSettings(settings), SETTINGS_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [settings]);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        PmsAPI.rateCalendar
            .index({
                year,
                base_years: baseYears,
                peak_holiday_run: debouncedSettings.peakHolidayRun,
                special_holiday_run: debouncedSettings.specialHolidayRun,
                peak_threshold: debouncedSettings.peakThreshold,
                target_occupancy: debouncedSettings.targetOccupancy,
                tolerance: debouncedSettings.tolerance,
                exclude_groups: debouncedSettings.excludeGroups,
            })
            .then((res) => {
                // setPayload 與 setLoading 放同一個 callback 裡一起觸發，
                // 讓 React 併成同一次重繪 —— 拆成 .then().finally() 兩段各自
                // 排一個微任務，會讓「資料到了」與「loading 關掉」隔一輪才發生，
                // 在測試裡容易變成沒被 act() 包住的殘留更新。
                if (cancelled) return;
                setPayload(res.data);
                setLoading(false);
            })
            .catch(() => {
                // 沒有訊息的失敗最難處理：轉圈停下、畫面停在上一次的結果，
                // 使用者會以為那就是新參數算出來的答案。匯出那條本來就有訊息，
                // 查詢這條反而沒有。
                if (cancelled) return;
                setLoading(false);
                message.error('查詢失敗，請確認參數後再試一次');
            });
        return () => {
            cancelled = true;
        };
    }, [year, baseYears, debouncedSettings]);

    /**
     * 建議清單的在手率，主查詢回來後另外發一次。
     *
     * 刻意不併進主查詢的 effect 裡序列等待 —— 這支查詢會逐日打 Oracle，比主查詢
     * 慢得多，等它會讓整張表跟著慢。清單先顯示，在手率後到，該欄位在資料未到時
     * 自己顯示佔位符，不擋住任何其他欄位。
     */
    React.useEffect(() => {
        const dates = (payload && payload.adjustments ? payload.adjustments : []).map((row) => row.date);
        if (!dates.length) {
            setPace({ status: 'idle', data: {}, truncated: false });
            return undefined;
        }

        // 清單長度天然就在上限以內，但那是資料決定的。超過就截斷並在欄名標示，
        // 比整欄被後端以 422 打掉、無聲空白好。
        const capped = dates.slice(0, MAX_PACE_DATES);
        const truncated = capped.length < dates.length;

        let cancelled = false;
        setPace({ status: 'loading', data: {}, truncated });
        PmsAPI.rateCalendar
            .pace(capped, [PACE_LEAD])
            .then((res) => {
                if (cancelled) return;
                setPace({ status: 'done', data: res.data || {}, truncated });
            })
            .catch(() => {
                // 在手率是輔助判準，查不到不該讓整張清單或整頁失敗，
                // 也不該再彈一個使用者無從處理的錯誤訊息 —— 該欄自己標示查詢失敗。
                if (cancelled) return;
                setPace({ status: 'failed', data: {}, truncated });
            });

        return () => {
            cancelled = true;
        };
    }, [payload]);

    const days = React.useMemo(
        () => resolveDays(payload ? payload.days : [], overrides),
        [payload, overrides]
    );

    const levelsByDate = React.useMemo(
        () => Object.fromEntries(days.map((day) => [day.date, day.level])),
        [days]
    );

    const changeSetting = (key, value) => {
        if (value === null || value === undefined) return;
        // 四個門檻是數字，排除團體是布林 —— 一律 Number() 會把 false 變成 0，
        // 送進 API 的 exclude_groups 就永遠是假值
        const next = typeof value === 'boolean' ? value : Number(value);
        setSettings(saveSettings({ ...settings, [key]: next }));
    };

    const changeLevel = (date, level) => {
        setOverrides(saveOverride(year, date, level));
    };

    // 只重設單一日期，改錯一天不必連累整年已經調好的其他日期。
    // 「重設」按鈕 (清整年) 維持原樣並存，兩者用途不同。
    const resetLevel = (date) => {
        setOverrides(clearOverride(year, date));
    };

    const exportXlsx = () => {
        PmsAPI.rateCalendar.exportXlsx(year, levelsByDate).then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `平旺假日表_${year}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
        }).catch(() => message.error('匯出失敗'));
    };

    const seriesOption = React.useMemo(() => {
        if (!payload) return null;
        const ranges = (payload.peak_ranges && payload.peak_ranges.core) || [];
        const series = payload.series || {};

        // 用後端回的 base_years，不是前端自己的 state：使用者清空取樣年度時後端會
        // 套用預設值，此時 state 是空陣列，拿它去索引 series 會讓 x 軸整個空掉、
        // 旺季色帶完全不出現。回應特地回了 base_years 就是為了這件事。
        const years = (payload.base_years || Object.keys(series))
            .map(String)
            .filter((y) => series[y]);

        // 各年的鍵集本來就不一樣 —— 2024 是閏年 (366 天)，Oracle 查無資料的日子
        // 也不會出現在 metrics 裡。用「陣列索引對座標軸索引」對齊時，2/29 之後每條
        // 線都會相對座標軸位移，而旺季色帶是用 MM-DD 定位的，色帶就對不上它要解釋
        // 的線 —— 這張圖正是旺季判定的視覺佐證。改成軸取各年鍵的聯集，各年的點用
        // [MM-DD, 住房率] 成對給值，由 echarts 依類目名對位。
        const axisSet = {};
        years.forEach((y) => {
            Object.keys(series[y]).forEach((md) => {
                axisSet[md] = true;
            });
        });
        const axis = Object.keys(axisSet).sort();

        return {
            tooltip: { trigger: 'axis' },
            legend: {},
            xAxis: { type: 'category', data: axis },
            yAxis: { type: 'value', name: '住房率 %', max: 100 },
            series: years.map((y) => ({
                name: `${y} 年`,
                type: 'line',
                smooth: true,
                showSymbol: false,
                data: Object.keys(series[y]).sort().map((md) => [md, series[y][md].occupancy]),
                // 旺季色帶疊在第一條線上就夠了，每條都疊會糊成一片
                markArea: y === years[0] ? {
                    itemStyle: { color: 'rgba(76, 175, 80, 0.15)' },
                    data: ranges.map((range) => [{ xAxis: range.from }, { xAxis: range.to }]),
                } : undefined,
            })),
        };
    }, [payload]);

    // 包 useMemo 是為了那個 []：字面量每次 render 都是新陣列，會讓下面
    // pendingAdjustments 的 useMemo 每次都重算，等於沒有記憶效果。
    const adjustments = React.useMemo(() => (payload ? payload.adjustments : []), [payload]);

    // 還沒採納的建議。已採納的要從「套用全部」的對象裡排除，否則按鈕上的
    // 筆數會一直停在總數，看不出還剩幾天沒處理，按下去也白做一次寫入。
    const pendingAdjustments = React.useMemo(
        () => adjustments.filter((row) => overrides[row.date] !== row.to),
        [adjustments, overrides]
    );

    /**
     * 整批採納：把還沒採納的建議一次寫進 overrides。
     *
     * 合併成一次 localStorage 寫入 (見 saveOverrides)，而不是逐日呼叫
     * changeLevel —— 八十幾筆就是八十幾次讀寫，中途出錯還會留下寫到一半的狀態。
     *
     * 採納後不關閉 Drawer：使用者通常會接著掃一遍確認哪幾天變了。
     */
    const adoptAll = () => {
        if (!pendingAdjustments.length) return;
        const levelByDate = {};
        pendingAdjustments.forEach((row) => {
            levelByDate[row.date] = row.to;
        });
        setOverrides(saveOverrides(year, levelByDate));
        message.success(`已套用 ${pendingAdjustments.length} 筆建議`);
    };

    /**
     * 把四個門檻數字讀回成人看得懂的一句話，取代原本只顯示「降級 < 60%
     * 升級 > 70%」的 threshold-preview —— 那個短顯示只講了目標／容忍帶，
     * 連假門檻與旺季敏感度兩組參數改了多少，畫面上完全看不出意義，使用者
     * 得自己在腦中換算，這裡把四個門檻的作用一次寫成一句話。
     *
     * 刻意讀 settings 而不是 debouncedSettings：這是純算術，不需要等重新
     * 查詢，四個輸入框一改這裡就要立刻跟著變 (debounce 延後的是查詢，不是
     * 這段文字，見 SETTINGS_DEBOUNCE_MS 的說明)。
     */
    const thresholdExplanation =
        `連續假期滿 ${settings.peakHolidayRun} 天列為旺假日、滿 ${settings.specialHolidayRun} 天列為大假日；` +
        `住房率 7 日移動平均高於當年中位數 ${settings.peakThreshold} 個百分點的連續區間視為旺季；` +
        `歷史住房率中位數低於 ${settings.targetOccupancy - settings.tolerance}% 建議降級，` +
        `高於 ${settings.targetOccupancy + settings.tolerance}% 建議升級。` +
        (settings.excludeGroups ? '住房率已排除旅行社團體，只計散客。' : '');

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small">
                {/* 條件參數在左、功能按鈕在右：兩者是不同意圖 (設定 vs. 動作)，混在
                    同一個 Row 裡會讓人分不出「這是在調參數」還是「這是要做動作」。
                    六組參數與三顆按鈕仍是同一層、扁平排列的 flex-wrap 項目 (呼應
                    改前 Row/Col 逐欄折行的寫法)，換行時每一行都能用滿整列寬度；
                    只在 controls-params／controls-actions 這兩個外殼套
                    display: 'contents' 取得可查詢的 DOM 邊界，但不讓外殼自己
                    參與寬度分配 —— 曾經改成「參數外殼」與「按鈕」兩個並排的 flex
                    項目，結果按鈕固定吃掉一段寬度，參數外殼被壓縮到剩餘寬度，
                    害參數在還沒到卡片右緣就提前折成三行，控制列反而比改前更高，
                    真實瀏覽器量測後才抓到這個問題，改回扁平結構就恢復兩行。
                    按鈕靠右對齊只在最後一個 flex 項目 (按鈕的 Space) 上加
                    marginLeft: 'auto'：這個項目之前的所有項目排不下時，被推去
                    的是它自己所在那一行的最右側，該行仍是整列滿寬，不必另寫
                    media query。 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
                    <div data-testid="controls-params" style={{ display: 'contents' }}>
                        <span>
                            目標年度
                            <Select
                                data-testid="year"
                                value={year}
                                onChange={setYear}
                                style={{ width: 100, marginLeft: 8 }}
                                options={[THIS_YEAR, THIS_YEAR + 1, THIS_YEAR + 2].map((y) => ({ value: y, label: y }))}
                            />
                        </span>
                        <span>
                            取樣年度
                            <Select
                                data-testid="base-years"
                                mode="multiple"
                                value={baseYears}
                                onChange={setBaseYears}
                                style={{ minWidth: 220, marginLeft: 8 }}
                                options={Array.from({ length: BASE_YEAR_CHOICES }, (_, i) => THIS_YEAR - 1 - i)
                                    .filter((y) => y >= EARLIEST_DATA_YEAR)
                                    .map((y) => ({ value: y, label: y }))}
                            />
                        </span>
                        <span>
                            連假門檻 (旺假日 / 大假日)
                            <InputNumber
                                data-testid="peak-holiday-run"
                                min={2} max={30} value={settings.peakHolidayRun}
                                onChange={(v) => changeSetting('peakHolidayRun', v)}
                                style={{ width: 64, marginLeft: 8 }}
                            />
                            <InputNumber
                                data-testid="special-holiday-run"
                                min={2} max={30} value={settings.specialHolidayRun}
                                onChange={(v) => changeSetting('specialHolidayRun', v)}
                                style={{ width: 64, marginLeft: 4 }}
                            />
                        </span>
                        <span>
                            旺季敏感度
                            <InputNumber
                                data-testid="peak-threshold"
                                min={0} max={100} value={settings.peakThreshold}
                                onChange={(v) => changeSetting('peakThreshold', v)}
                                style={{ width: 72, marginLeft: 8 }}
                            />
                        </span>
                        <span>
                            目標住房率 %
                            <InputNumber
                                data-testid="target-occupancy"
                                min={0} max={100} value={settings.targetOccupancy}
                                onChange={(v) => changeSetting('targetOccupancy', v)}
                                style={{ width: 72, marginLeft: 8 }}
                            />
                        </span>
                        {/* 旅行社包團走合約價，抬高牌價收不到那筆錢 —— 排除之後
                            住房率反映的是散客需求。實測團體占比在四、五月高達
                            23~33%，暑假只有 1~10%，所以這個開關幾乎不動暑假，
                            專門處理春季那種靠包團撐起來的假旺季。 */}
                        <span>
                            <Checkbox
                                data-testid="exclude-groups"
                                checked={settings.excludeGroups}
                                onChange={(e) => changeSetting('excludeGroups', e.target.checked)}
                            >
                                排除旅行社團體
                            </Checkbox>
                        </span>
                        <span>
                            容忍帶
                            <InputNumber
                                data-testid="tolerance"
                                min={0} max={50} value={settings.tolerance}
                                onChange={(v) => changeSetting('tolerance', v)}
                                style={{ width: 64, marginLeft: 8 }}
                            />
                        </span>
                    </div>

                    <div data-testid="controls-actions" style={{ display: 'contents' }}>
                        <Space size={8} wrap style={{ marginLeft: 'auto' }}>
                            {/* 調整建議清單收在 Drawer 裡，這裡是唯一的入口，筆數要一眼看到 ——
                                使用者靠這個數字判斷「還有多少沒看過」，不用打開才知道。 */}
                            <Button
                                data-testid="adjustments-drawer-trigger"
                                onClick={() => setAdjustmentsOpen(true)}
                            >
                                {`調整建議 (${adjustments.length})`}
                            </Button>
                            {/* 送出的 levels 必須涵蓋目標年整年，否則後端 422，
                                而使用者只看得到籠統的「匯出失敗」。三種情況都要擋：
                                第一次查詢還沒回來 (days 是空陣列)、查詢進行中，
                                以及切換目標年後資料還沒換過來 —— 那時 year 已是
                                新值但 payload 還是舊年的 days，levelsByDate 整組
                                都是舊年的日期，必定 422。 */}
                            <Button
                                data-testid="export-xlsx"
                                type="primary"
                                onClick={exportXlsx}
                                disabled={!payload || loading || payload.year !== year}
                            >
                                匯出 Excel
                            </Button>
                            <Button data-testid="clear-overrides" onClick={() => setOverrides(clearOverrides(year))}>
                                重設
                            </Button>
                        </Space>
                    </div>
                </div>

                {/* 四個門檻各自改了多少，讀回成一句話，取代原本另立一格的
                    「降級 < 60%　升級 > 70%」——那個短顯示只講了目標／容忍帶，
                    現在併進這裡就不必兩處重複。樣式是輔助說明的層級 (較淡的
                    文字、字級較小)，不跟參數本身搶視覺。 */}
                <Typography.Paragraph
                    type="secondary"
                    data-testid="threshold-explanation"
                    style={{ marginTop: 8, marginBottom: 0, fontSize: FONT.caption }}
                >
                    {thresholdExplanation}
                </Typography.Paragraph>
            </Card>

            {(payload && payload.warnings ? payload.warnings : []).map((text) => (
                <Alert key={text} type="warning" showIcon message={text} />
            ))}

            {/* 三個已知限制要顯示在頁面上，不能只寫在設計文件裡。
                版面與上面的 warning 一致 (showIcon + message，不用 description)：
                兩者講的是同一類事情 —— 這張表有哪些地方不能全信 —— 用同一個
                視覺層級才不會讓人以為其中一個比較不重要。維持 info 而非 warning：
                上面那條是「這次查詢真的遇到了」的狀況，這條是恆常的使用須知，
                顏色分開才看得出哪一條需要當下處理。 */}
            <Alert
                type="info"
                showIcon
                message={
                    <>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>這一頁能做到與做不到的事</div>
                        <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                            <li>旺季區間是從取樣年度的住房率反推的。熱氣球嘉年華這類活動系統沒有檔期資料，明年日期若異動請自行對照當年公布的活動日程平移。</li>
                            <li>跨年夜的判定需要隔年的官方日曆，隔年檔案約前一年年中才上架，未上架前該日以星期規則推定。</li>
                            <li>人工調整只存在這台電腦的瀏覽器，換裝置或清除瀏覽器資料就會消失。排完請記得匯出。</li>
                        </ul>
                    </>
                }
            />

            <Card title={`${year} 年平旺假日表`} size="small">
                {/* 圖例移出 Card 的 extra，改成標題下方獨立一列：這頁的產出是一張彩色
                    編碼的日曆，圖例是讀懂整頁的鑰匙，不能要求使用者逐格 hover 才看得懂，
                    現在要放七件事 (五個級別 + 底線 + 藍框 + 黑點)，塞進 extra 那個跟
                    標題搶同一行的窄空間，窄視窗會被壓爆或整排被裁掉；獨立一列配
                    Space 的 wrap，放不下時自然換行，不會擠壓標題或被裁切。
                    色塊 + 簡寫文字：簡寫 (平/旺/假/旺假/大假) 不會跟下拉選單的完整
                    名稱逐字相同，title / aria-label 補上完整名稱給滑鼠停留與螢幕
                    閱讀器用。aria-label 需要一個非 generic 的 role 才會被採計為
                    accessible name 的來源，plain <span> (role=generic) 不算數，
                    部分螢幕閱讀器只會讀到可見的簡寫文字 —— 補 role="img" 讓這一格
                    被當成單一整體。 */}
                <Space data-testid="calendar-legend" size={10} wrap style={{ marginBottom: 12 }}>
                    {Object.keys(LEVEL_LABELS).map((level) => (
                        <span
                            key={level}
                            role="img"
                            title={LEVEL_LABELS[level]}
                            aria-label={LEVEL_LABELS[level]}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: FONT.caption }}
                        >
                            <span
                                aria-hidden="true"
                                style={{
                                    display: 'inline-block',
                                    width: 12,
                                    height: 12,
                                    borderRadius: 2,
                                    background: LEVEL_COLORS[level],
                                    border: '1px solid #d9d9d9',
                                }}
                            />
                            {LEVEL_ABBR[level]}
                        </span>
                    ))}
                    {/* 底線是與級別正交的第四個通道 (見 YearCalendar 檔頭 docblock)：
                        日期數字加底線代表這天有節日名稱，不代表級別較高，圖例要單獨
                        說明，不能只靠色塊列表讓人猜。 */}
                    <span style={{ fontSize: FONT.caption }}>
                        <span style={{ textDecoration: 'underline', fontWeight: 700 }}>底線</span>
                        {' = 特別假日'}
                    </span>
                    {/* 藍框與黑點是使用者反覆問過兩次的兩個通道 (見 YearCalendar 檔頭
                        docblock)：藍框代表有建議但尚未採納 (底色仍是規則級別)，黑點
                        代表已經人工指定過。樣式直接吃 SUGGESTED_BORDER /
                        MANUAL_DOT_STYLE 這兩個跟日曆格子共用的常數，不另外寫一份色碼，
                        以後改一邊不會忘了改另一邊。 */}
                    <span
                        data-testid="legend-suggested"
                        role="img"
                        title="有建議，尚未採納 —— 底色仍是規則算出的級別，到下方建議清單按「採納」才會套用"
                        aria-label="藍框：資料對這天有建議，尚未採納"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: FONT.caption }}
                    >
                        <span
                            data-testid="legend-suggested-swatch"
                            aria-hidden="true"
                            style={{
                                display: 'inline-block',
                                boxSizing: 'border-box',
                                width: 12,
                                height: 12,
                                borderRadius: 2,
                                background: '#ffffff',
                                border: SUGGESTED_BORDER,
                            }}
                        />
                        藍框 = 有建議
                    </span>
                    <span
                        data-testid="legend-manual"
                        role="img"
                        title="人工指定過 (自己選的，或按了建議清單的「採納」)，一律覆蓋規則與建議"
                        aria-label="黑點：人工指定過，一律覆蓋規則與建議"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: FONT.caption }}
                    >
                        <span
                            aria-hidden="true"
                            style={{
                                position: 'relative',
                                display: 'inline-block',
                                width: 12,
                                height: 12,
                                borderRadius: 2,
                                background: '#ffffff',
                                border: '1px solid #d9d9d9',
                            }}
                        >
                            <span data-testid="legend-manual-dot" style={MANUAL_DOT_STYLE} />
                        </span>
                        黑點 = 人工指定
                    </span>
                </Space>

                {/* 不用 Card 的 loading 骨架 —— 骨架會把日曆整組從畫面拔掉，
                    門檻設定一改就整片閃空。改用 Spin 蓋一層轉圈，格子留在原地，
                    使用者才看得到自己剛才調整過的地方，重新查詢也不會覆寫人工指定。 */}
                <Spin spinning={loading}>
                    <YearCalendar year={year} days={days} onChangeLevel={changeLevel} onReset={resetLevel} />
                </Spin>
            </Card>

            {/* 2027 年有 92 筆建議，整張表放在頁面下方的 Card 裡，把日曆擠到畫面最上面
                一小塊，反而是這頁真正該看的東西被擠掉。改用 Drawer：控制列的按鈕
                是唯一入口 (筆數印在按鈕上，不用點開才知道有多少)，表格本身、欄位、
                採納按鈕、已採納的 disabled 狀態都原封不動搬過來。
                寬度定 880px 內用 CSS min()，落在「放得下這張表」的 720~900px
                建議範圍，同時窄視窗會自動收到 100vw，不必另外寫 resize 監聽。
                不設 destroyOnClose —— 內容不是 Drawer 自己查的 (資料是 payload/pace
                這兩份 state，Drawer 開關不影響它們)，關閉時卸載重掛只是白工。
                採納後刻意不關閉 —— 使用者通常連續處理好幾筆，關一次點一次太煩。 */}
            <Drawer
                data-testid="adjustments-drawer"
                title={`調整建議 (${adjustments.length})`}
                placement="right"
                width="min(880px, 100vw)"
                visible={adjustmentsOpen}
                onClose={() => setAdjustmentsOpen(false)}
            >
                {/* 建議預設不套用，日曆顯示的一律是規則算出的級別。這句話必須寫在畫面上：
                    使用者若誤以為建議已經生效，匯出的內容就會跟他以為的不一樣。
                    用 Alert 而不是淡灰小字 —— 「建議不會自動套用」是會直接影響匯出結果的
                    前提，淡到像註腳反而最容易被略過。 */}
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="以下是資料算出的調整建議，不會自動套用。上方日曆顯示的一律是規則算出的級別；同意某一條就按「採納」，那一天才會改成建議的級別，匯出時也才會送出去。按「還原」可以取消已採納的那一天。"
                />
                {/* 整批採納放在表格上方而不是控制列：它的作用對象是這張清單，
                    放在清單旁邊才看得出「全部」指的是哪些。筆數印在按鈕上，
                    按下去會動到幾天一目了然。
                    刻意不做「全部還原」——上方控制列的「重設」已經是清掉整年的
                    人工調整，兩顆語意重疊的按鈕只會讓人猶豫該按哪一顆。 */}
                <div style={{ marginBottom: 12, textAlign: 'right' }}>
                    <Button
                        data-testid="adopt-all"
                        onClick={adoptAll}
                        disabled={pendingAdjustments.length === 0}
                    >
                        套用全部建議 ({pendingAdjustments.length})
                    </Button>
                </div>
                <Table
                    rowKey="date"
                    size="small"
                    pagination={false}
                    dataSource={adjustments}
                    onRow={(row) => ({ 'data-testid': `adjustment-${row.date}` })}
                    // 固定欄 (除「理由」外) 的寬度加總是 760 (日期 200 + 調整 150 +
                    // 住房率 130 + 在手率 150 + 操作 130)，Drawer 預設寬度扣掉本體
                    // padding 後裝得下，這裡仍給 scroll.x 當安全網 —— 視窗比 Drawer
                    // 預設窄 (行動裝置的 100vw) 時用水平捲動，不要讓欄寬被壓縮回去斷行。
                    scroll={{ x: 920 }}
                    columns={[
                        {
                            // 與其他頁一致用 DateWithWeekday：日期本身看不出星期與身分，
                            // 而「這天是不是例假日／特別假日／補班」正是判斷該不該調整
                            // 級別的前提。元件會自己標例假日、特別假日與補班日。
                            // 寬度放寬到 200：除了日期與星期，還要容得下一個標籤不斷行。
                            title: '日期',
                            dataIndex: 'date',
                            width: 200,
                            onHeaderCell: () => NOWRAP_CELL,
                            onCell: () => NOWRAP_CELL,
                            render: (date) => <DateWithWeekday date={date} />,
                        },
                        {
                            // 最長組合「旺假日 → 大假日」9 個全形字元 + 箭頭，量測後 150px 不斷行。
                            title: '調整',
                            width: 150,
                            onHeaderCell: () => NOWRAP_CELL,
                            onCell: () => NOWRAP_CELL,
                            render: (_, row) => `${LEVEL_LABELS[row.from]} → ${LEVEL_LABELS[row.to]}`,
                        },
                        {
                            // 表頭「住房率中位數」6 個全形字元比三位數 (100.0%) 的內容還寬，
                            // 是這欄的寬度瓶頸。
                            title: '住房率中位數',
                            dataIndex: 'occupancy_median',
                            width: 130,
                            onHeaderCell: () => NOWRAP_CELL,
                            onCell: () => NOWRAP_CELL,
                            render: (v) => (v == null ? '-' : `${v}%`),
                        },
                        {
                            // 住房率撞天花板 (旺日與旺假日常常都在 95~100%) 時，
                            // 在手率是唯一分得出高下的判準，而那正是這張表最難排的部分。
                            //
                            // 表頭縮短成「提前 60 天在手率」以留住足夠寬度不斷行；
                            // 「僅前 60 筆」的但書是真的限制 (訂房速度查詢一次最多
                            // MAX_PACE_DATES 個日期)，不能就此消失，改放進 Tooltip——
                            // Drawer 上方的說明文字已經解釋過整張清單的性質，這裡
                            // 只補這一欄特有的截斷但書即可。
                            title: pace.truncated ? (
                                <Tooltip title={`一次最多查 ${MAX_PACE_DATES} 個日期，僅前 ${MAX_PACE_DATES} 筆有在手率資料，其餘顯示「—」`}>
                                    <span>{`提前 ${PACE_LEAD} 天在手率`}</span>
                                </Tooltip>
                            ) : `提前 ${PACE_LEAD} 天在手率`,
                            key: 'pace',
                            dataIndex: 'date',
                            width: 150,
                            onHeaderCell: () => NOWRAP_CELL,
                            onCell: () => NOWRAP_CELL,
                            render: (date) => {
                                if (pace.status === 'failed') return '查詢失敗';
                                const row = pace.data[date];
                                const value = row ? row[PACE_LEAD] : undefined;
                                if (value == null) return pace.status === 'loading' ? '查詢中' : '—';
                                return `${value}%`;
                            },
                        },
                        // 這是唯一允許換行的欄位：說明文字長短不一，寬度不夠也不該被
                        // 硬擠成單行，讓它吃剩下的空間即可。
                        { title: '理由', dataIndex: 'reason' },
                        {
                            title: '操作',
                            width: 130,
                            onHeaderCell: () => NOWRAP_CELL,
                            onCell: () => NOWRAP_CELL,
                            render: (_, row) => {
                                // 建議預設不生效，「採納」把 row.to (建議的級別) 寫進
                                // overrides，那一天變成人工指定。已採納的要標示出來、
                                // 擋掉重複點，不然使用者分不出哪些已經處理過。
                                const adopted = overrides[row.date] === row.to;
                                // 「還原」清掉這一天的 override，讓它退回規則算出的級別。
                                // 採納是會改變匯出結果的動作，沒有回頭路的話使用者只能
                                // 整年重設 (上方那顆「重設」)，連帶把其他已經調好的日子
                                // 一起清掉。只有這天真的有人工指定時才可按 —— 沒有東西
                                // 可還原時 disabled，不讓人按了以為有效果。
                                const hasOverride = overrides[row.date] != null;
                                return (
                                    <Space size={4}>
                                        <Button
                                            size="small"
                                            data-testid={`adopt-${row.date}`}
                                            disabled={adopted}
                                            onClick={() => changeLevel(row.date, row.to)}
                                        >
                                            {adopted ? '已採納' : '採納'}
                                        </Button>
                                        <Button
                                            size="small"
                                            data-testid={`revert-${row.date}`}
                                            disabled={!hasOverride}
                                            onClick={() => setOverrides(clearOverride(year, row.date))}
                                        >
                                            還原
                                        </Button>
                                    </Space>
                                );
                            },
                        },
                    ]}
                />
            </Drawer>

            <Card title="逐日住房率與偵測出的旺季區間" size="small">
                <ResponsiveECharts option={seriesOption} xs={240} md={320} />
            </Card>
        </Space>
    );
}
